import React, {useEffect, useState, useCallback} from 'react';
import {View, ScrollView, StyleSheet, TouchableOpacity} from 'react-native';
import {Appbar, TextInput, Button, Snackbar, Text, Chip} from 'react-native-paper';
import {useTranslation} from 'react-i18next';
import {useNavigation} from '@react-navigation/native';
import {useConfigStore} from '../../store/useConfigStore';
import {colors, spacing} from '../../theme';

const PaymentSettingsScreen = () => {
  const {t} = useTranslation();
  const navigation = useNavigation();
  const {fetchByType, getProperty, updateProperties, isLoading} = useConfigStore();

  const [modes, setModes] = useState([]);
  const [newMode, setNewMode] = useState('');
  const [maxAdjustable, setMaxAdjustable] = useState('');
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState('');

  useEffect(() => {
    fetchByType('PAYMENT');
  }, [fetchByType]);

  useEffect(() => {
    const raw = getProperty('PAYMENT', 'PAYMENT_MODES') || '';
    const parsed = raw.split(',').map(s => s.trim()).filter(Boolean);
    setModes(parsed);
    setMaxAdjustable(getProperty('PAYMENT', 'MAX_AMOUNT_ADJUSTABLE'));
  }, [getProperty, useConfigStore.getState().properties]);

  const removeMode = useCallback((index) => {
    setModes(prev => prev.filter((_, i) => i !== index));
  }, []);

  const addMode = useCallback(() => {
    const trimmed = newMode.trim();
    if (!trimmed) return;
    if (modes.some(m => m.toUpperCase() === trimmed.toUpperCase())) {
      setSnackbar('Payment mode already exists');
      return;
    }
    setModes(prev => [...prev, trimmed]);
    setNewMode('');
  }, [newMode, modes]);

  const handleSave = useCallback(async () => {
    setSaving(true);
    try {
      await updateProperties('PAYMENT', [
        {code: 'PAYMENT_MODES', name: 'Payment Modes', value: modes.join(',')},
        {code: 'MAX_AMOUNT_ADJUSTABLE', name: 'Max Amount Adjustable', value: maxAdjustable},
      ]);
      setSnackbar(t('config.saved'));
    } catch (e) {
      setSnackbar(e.response?.data?.message || t('common.error'));
    } finally {
      setSaving(false);
    }
  }, [modes, maxAdjustable, updateProperties, t]);

  return (
    <View style={styles.container}>
      <Appbar.Header style={styles.appbar} dark>
        <Appbar.BackAction onPress={() => navigation.goBack()} color={colors.white} />
        <Appbar.Content title={t('config.paymentSettings')} titleStyle={styles.appbarTitle} />
      </Appbar.Header>

      <ScrollView style={styles.content} keyboardShouldPersistTaps="handled">
        <Text variant="labelLarge" style={styles.sectionTitle}>
          {t('config.paymentModes').toUpperCase()}
        </Text>
        <Text variant="bodySmall" style={styles.description}>{t('config.paymentModesDesc')}</Text>

        <View style={styles.tagsContainer}>
          {modes.map((mode, index) => (
            <Chip
              key={`${mode}-${index}`}
              style={styles.tag}
              textStyle={styles.tagText}
              closeIcon="close-circle"
              onClose={() => removeMode(index)}>
              {mode}
            </Chip>
          ))}
          <View style={styles.addModeRow}>
            <TextInput
              value={newMode}
              onChangeText={setNewMode}
              placeholder={t('config.addPaymentMode')}
              mode="outlined"
              dense
              style={styles.addModeInput}
              outlineColor={colors.border}
              activeOutlineColor={colors.primary}
              onSubmitEditing={addMode}
              returnKeyType="done"
            />
            <TouchableOpacity
              style={[styles.addButton, !newMode.trim() && styles.addButtonDisabled]}
              onPress={addMode}
              disabled={!newMode.trim()}
              activeOpacity={0.7}>
              <Text style={styles.addButtonText}>+</Text>
            </TouchableOpacity>
          </View>
        </View>

        <Text variant="labelLarge" style={[styles.sectionTitle, {marginTop: spacing.lg}]}>
          {t('config.maxAdjustable').toUpperCase()}
        </Text>
        <Text variant="bodySmall" style={styles.description}>{t('config.maxAdjustableDesc')}</Text>
        <TextInput
          label={t('config.maxAdjustable')}
          value={maxAdjustable}
          onChangeText={setMaxAdjustable}
          mode="outlined"
          keyboardType="numeric"
          style={styles.input}
        />

        <Button
          mode="contained"
          onPress={handleSave}
          loading={saving}
          disabled={saving || isLoading}
          style={styles.saveBtn}
          buttonColor={colors.primary}>
          {t('common.save')}
        </Button>
      </ScrollView>

      <Snackbar visible={!!snackbar} onDismiss={() => setSnackbar('')} duration={2000}>
        {snackbar}
      </Snackbar>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {flex: 1, backgroundColor: colors.background},
  appbar: {backgroundColor: colors.primary},
  appbarTitle: {color: colors.white},
  content: {padding: spacing.md},
  sectionTitle: {color: colors.textSecondary, marginBottom: spacing.xs},
  description: {color: colors.textSecondary, marginBottom: spacing.sm},
  input: {marginBottom: spacing.sm, backgroundColor: colors.surface},
  saveBtn: {marginTop: spacing.md, marginBottom: spacing.xxl},
  tagsContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: spacing.xs,
    backgroundColor: colors.surface,
    borderWidth: 1,
    borderColor: colors.border,
    borderRadius: 10,
    padding: spacing.sm,
    marginBottom: spacing.sm,
  },
  tag: {
    backgroundColor: colors.primary + '15',
    borderColor: colors.primary + '30',
    borderWidth: 1,
  },
  tagText: {
    fontSize: 14,
    color: colors.text,
  },
  addModeRow: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
    minWidth: 150,
    gap: spacing.xs,
  },
  addModeInput: {
    flex: 1,
    backgroundColor: colors.surface,
    height: 38,
    fontSize: 14,
  },
  addButton: {
    width: 36,
    height: 36,
    borderRadius: 18,
    backgroundColor: colors.primary,
    justifyContent: 'center',
    alignItems: 'center',
  },
  addButtonDisabled: {
    backgroundColor: colors.border,
  },
  addButtonText: {
    color: colors.white,
    fontSize: 20,
    fontWeight: '600',
    marginTop: -1,
  },
});

export default PaymentSettingsScreen;
